export const MERCHANDISING_CONTRACT_VERSION = 'merchandising.v1';
export const MERCHANDISING_PREVIEW_TIMEOUT_MS = 4000;
export const MERCHANDISING_DEFAULT_TIMEOUT_MS = 30000;

export class MerchandisingApiError extends Error {
  constructor({
    code = 'MERCHANDISING_REQUEST_FAILED',
    message = 'Merchandising request failed.',
    details = [],
    meta = {},
    currentRevision = null,
    recovery = null,
    cause = null,
  } = {}) {
    super(message);
    this.name = 'MerchandisingApiError';
    this.code = code;
    this.details = Array.isArray(details) ? details : [];
    this.meta = meta && typeof meta === 'object' ? meta : {};
    this.requestId = this.meta.request_id || null;
    this.currentRevision = currentRevision;
    this.recovery = recovery;
    this.cause = cause;
  }
}

const asError = (payload, cause = null) => {
  if (payload instanceof MerchandisingApiError) return payload;
  const source = payload && typeof payload === 'object' ? payload : {};
  const operationDetails = Array.isArray(source.details)
    ? source.details.find((detail) => detail && typeof detail === 'object'
      && (detail.current_revision != null || detail.currentRevision != null || detail.recovery != null))
    : null;
  return new MerchandisingApiError({
    code: source.code || source.errorCode || 'MERCHANDISING_REQUEST_FAILED',
    message: source.error || source.message || 'Merchandising request failed.',
    details: source.details,
    meta: source.meta,
    currentRevision: operationDetails?.current_revision
      ?? operationDetails?.currentRevision
      ?? source.current_revision
      ?? source.currentRevision
      ?? null,
    recovery: operationDetails?.recovery ?? source.recovery ?? null,
    cause,
  });
};

export const unwrapMerchandisingEnvelope = (response) => {
  if (!response || typeof response !== 'object') {
    throw asError({ code: 'INVALID_RESPONSE', error: 'The merchandising service returned an invalid response.' });
  }
  if (response.result === 'error') throw asError(response);
  if (response.result !== 'success') {
    throw asError({ code: 'INVALID_RESPONSE', error: 'The merchandising service returned an unknown envelope.' });
  }
  return {
    data: response.data && typeof response.data === 'object' ? response.data : {},
    meta: response.meta && typeof response.meta === 'object' ? response.meta : {},
  };
};

const ensureObject = (value, label) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new TypeError(`${label} must be an object.`);
  }
};

const ensureId = (value, label) => {
  if (typeof value !== 'string' || value.length === 0) {
    throw new TypeError(`${label} must be a non-empty string.`);
  }
};

const ensureRevision = (value) => {
  if (!Number.isInteger(value) || value < 0) {
    throw new TypeError('revision must be a non-negative integer.');
  }
};

const keyDescriptors = (descriptors, key) => {
  if (!Array.isArray(descriptors)) return descriptors || {};
  return Object.fromEntries(
    descriptors
      .filter((descriptor) => descriptor && typeof descriptor[key] === 'string')
      .map((descriptor) => [descriptor[key], descriptor]),
  );
};

export const normalizeMerchandisingSchema = (schema) => ({
  ...schema,
  condition_fields: keyDescriptors(schema?.condition_fields, 'key'),
  actions: keyDescriptors(schema?.actions, 'type'),
});

export const MERCHANDISING_READ_ACTIONS = new Set([
  'merchandising.schema.get',
  'merchandising.condition_values.search',
  'merchandising.category_values.search',
  'merchandising.rules.list',
  'merchandising.rules.get',
  'merchandising.query_groups.list',
  'merchandising.query_groups.get',
  'merchandising.categories.list',
  'merchandising.index_state.get',
  'merchandising.explain.product',
  'merchandising.explain.category',
  'merchandising.preview.index_rule',
  'merchandising.preview.query',
  'merchandising.csv.export',
  'merchandising.csv.import_preview',
]);

const WRITE_ACTIONS = new Set([
  'merchandising.rules.create',
  'merchandising.rules.update',
  'merchandising.rules.set_status',
  'merchandising.rules.archive',
  'merchandising.rules.delete',
  'merchandising.rules.validate',
  'merchandising.query_groups.create',
  'merchandising.query_groups.update',
  'merchandising.query_groups.set_status',
  'merchandising.query_groups.delete',
  'merchandising.categories.save_manual',
  'merchandising.categories.clear_manual',
  'merchandising.categories.review_ai',
  'merchandising.csv.import_apply',
]);

export const MERCHANDISING_ACTIONS = new Set([...MERCHANDISING_READ_ACTIONS, ...WRITE_ACTIONS]);

/**
 * Transport-neutral merchandising API. The supplied transport receives only
 * allowlisted requirement-60 actions and returns the raw bridge envelope.
 */
export function createMerchandisingApi({ send }) {
  if (typeof send !== 'function') throw new TypeError('send must be a function.');

  const invoke = async (action, payload = {}, timeout = MERCHANDISING_DEFAULT_TIMEOUT_MS) => {
    if (!MERCHANDISING_ACTIONS.has(action)) {
      throw new TypeError(`Unsupported merchandising action: ${action}`);
    }
    ensureObject(payload, 'payload');
    try {
      const result = unwrapMerchandisingEnvelope(await send(action, payload, timeout));
      return { ...result.data, meta: result.meta };
    } catch (error) {
      throw asError(error, error);
    }
  };

  return {
    getSchema: async (languageId = null) => normalizeMerchandisingSchema(
      await invoke('merchandising.schema.get', { language_id: languageId }),
    ),
    searchConditionValues: ({ field, query = '', languageId = null, cursor = null, limit = 20 }) =>
      invoke('merchandising.condition_values.search', {
        field, query, language_id: languageId, cursor, limit,
      }),
    searchCategoryValues: ({ query = '', languageId = null, cursor = null, limit = 20 } = {}) =>
      invoke('merchandising.category_values.search', {
        query, language_id: languageId, cursor, limit,
      }),
    listRules: (filters = {}) => invoke('merchandising.rules.list', filters),
    getRule: (id) => {
      ensureId(id, 'id');
      return invoke('merchandising.rules.get', { id });
    },
    validateRule: (rule) => {
      ensureObject(rule, 'rule');
      return invoke('merchandising.rules.validate', { rule });
    },
    createRule: ({ clientMutationId, rule }) => {
      ensureId(clientMutationId, 'clientMutationId');
      ensureObject(rule, 'rule');
      return invoke('merchandising.rules.create', { client_mutation_id: clientMutationId, rule });
    },
    updateRule: ({ id, revision, rule }) => {
      ensureId(id, 'id');
      ensureRevision(revision);
      ensureObject(rule, 'rule');
      return invoke('merchandising.rules.update', { id, revision, rule });
    },
    setRuleStatus: ({ id, revision, status }) => {
      ensureId(id, 'id');
      ensureRevision(revision);
      return invoke('merchandising.rules.set_status', { id, revision, status });
    },
    archiveRule: ({ id, revision }) => {
      ensureId(id, 'id');
      ensureRevision(revision);
      return invoke('merchandising.rules.archive', { id, revision });
    },
    deleteRule: ({ id, revision }) => {
      ensureId(id, 'id');
      ensureRevision(revision);
      return invoke('merchandising.rules.delete', { id, revision });
    },
    listQueryGroups: ({ languageId = null, cursor = null, limit = 100 } = {}) =>
      invoke('merchandising.query_groups.list', { language_id: languageId, cursor, limit }),
    getQueryGroup: ({ id, languageId = null }) => {
      ensureId(id, 'id');
      return invoke('merchandising.query_groups.get', { id, language_id: languageId });
    },
    createQueryGroup: ({ clientMutationId, languageId = null, queries, rule, name = null, note = null }) => {
      ensureId(clientMutationId, 'clientMutationId');
      ensureObject(rule, 'rule');
      if (!Array.isArray(queries)) throw new TypeError('queries must be an array.');
      return invoke('merchandising.query_groups.create', {
        client_mutation_id: clientMutationId,
        language_id: languageId,
        queries,
        rule,
        name,
        note,
      });
    },
    updateQueryGroup: ({ id, revision, languageId = null, queries, rule, name = null, note = null }) => {
      ensureId(id, 'id');
      ensureRevision(revision);
      ensureObject(rule, 'rule');
      if (!Array.isArray(queries)) throw new TypeError('queries must be an array.');
      return invoke('merchandising.query_groups.update', {
        id, revision, language_id: languageId, queries, rule, name, note,
      });
    },
    setQueryGroupStatus: ({ id, revision, status, languageId = null }) => {
      ensureId(id, 'id');
      ensureRevision(revision);
      return invoke('merchandising.query_groups.set_status', {
        id, revision, status, language_id: languageId,
      });
    },
    deleteQueryGroup: ({ id, revision, languageId = null }) => {
      ensureId(id, 'id');
      ensureRevision(revision);
      return invoke('merchandising.query_groups.delete', {
        id, revision, language_id: languageId,
      });
    },
    exportCsv: ({ type, ids, languageId = null }) => {
      if (!['product_ranking', 'query_rules'].includes(type)) throw new TypeError('Unsupported CSV type.');
      if (!Array.isArray(ids) || ids.length < 1 || ids.length > 100) throw new TypeError('ids must contain 1-100 items.');
      return invoke('merchandising.csv.export', {
        type, ids: ids.map(String), language_id: languageId,
      });
    },
    previewCsv: ({ type, csv, languageId = null }) => {
      if (!['product_ranking', 'query_rules'].includes(type)) throw new TypeError('Unsupported CSV type.');
      if (typeof csv !== 'string' || !csv.length) throw new TypeError('csv must be a non-empty string.');
      return invoke('merchandising.csv.import_preview', {
        type, csv, language_id: languageId,
      });
    },
    importCsv: ({ type, csv, clientMutationId, languageId = null }) => {
      if (!['product_ranking', 'query_rules'].includes(type)) throw new TypeError('Unsupported CSV type.');
      if (typeof csv !== 'string' || !csv.length) throw new TypeError('csv must be a non-empty string.');
      ensureId(clientMutationId, 'clientMutationId');
      return invoke('merchandising.csv.import_apply', {
        type, csv, client_mutation_id: clientMutationId, language_id: languageId,
      });
    },
    listCategories: ({ languageId = null, includeImages = true } = {}) =>
      invoke('merchandising.categories.list', {
        language_id: languageId, include_images: includeImages,
      }),
    saveManualCategories: (changes) => invoke('merchandising.categories.save_manual', {
      language_scope: 'neutral', changes,
    }),
    clearManualCategories: (changes) => invoke('merchandising.categories.clear_manual', {
      language_scope: 'neutral', changes,
    }),
    reviewAiCategory: ({ ruleId, revision, decision }) => {
      ensureId(ruleId, 'ruleId');
      ensureRevision(revision);
      return invoke('merchandising.categories.review_ai', {
        rule_id: ruleId, revision, decision,
      });
    },
    getIndexState: (languageId = null) =>
      invoke('merchandising.index_state.get', { language_id: languageId }),
    explainProduct: ({ languageId = null, productId }) => {
      ensureId(productId, 'productId');
      return invoke('merchandising.explain.product', {
        language_id: languageId, product_id: productId,
      });
    },
    explainCategory: ({ languageId = null, categoryId }) => {
      ensureId(categoryId, 'categoryId');
      return invoke('merchandising.explain.category', {
        language_id: languageId, category_id: categoryId,
      });
    },
    previewIndexRule: (payload) =>
      invoke('merchandising.preview.index_rule', payload, MERCHANDISING_PREVIEW_TIMEOUT_MS),
    previewQuery: (payload) =>
      invoke('merchandising.preview.query', payload, MERCHANDISING_PREVIEW_TIMEOUT_MS),
  };
}

export function createUnavailableMerchandisingApi() {
  const unavailable = () => Promise.reject(new MerchandisingApiError({
    code: 'CAPABILITY_MISSING',
    message: 'Merchandising is unavailable on the current transport.',
  }));
  return Object.fromEntries([
    'getSchema', 'searchConditionValues', 'searchCategoryValues', 'listRules', 'getRule',
    'validateRule', 'createRule', 'updateRule', 'setRuleStatus', 'archiveRule', 'deleteRule',
    'listQueryGroups', 'getQueryGroup', 'createQueryGroup', 'updateQueryGroup', 'setQueryGroupStatus',
    'deleteQueryGroup',
    'exportCsv', 'previewCsv', 'importCsv',
    'listCategories', 'saveManualCategories', 'clearManualCategories', 'reviewAiCategory',
    'getIndexState', 'explainProduct', 'explainCategory', 'previewIndexRule', 'previewQuery',
  ].map((name) => [name, unavailable]));
}
